import type { Signal, TemplateRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FormBuilder } from '@angular/forms';

import { PushPipe } from '@ngrx/component';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  emailTrimValidator,
  isValidPhoneNumber,
  phoneNumberValidatorFn,
  softUrlValidator,
  URL_PROTOCOL_PATTERN,
} from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { CandidateInformationForm } from '@recruitee/offer-candidate-types';
import {
  ButtonComponent,
  CollapsibleCardComponent,
  HeadingComponent,
  IconComponent,
  PhoneNumberPipe,
  RtTooltip,
  SocialMediaPipe,
} from '@recruitee/user-interface';
import { bind } from '@recruitee/utils';

import { InformationField } from './candidate-profile-information.enums';
import type { CandidateFieldRow } from '../candidate-field-row/candidate-field-row.component';
import { CandidateFieldRowComponent } from '../candidate-field-row/candidate-field-row.component';
import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';
import { ProfileInformationEditComponent } from '../profile-information-edit/profile-information-edit.component';

const addHttpProtocolToUrl = (url: string) =>
  !URL_PROTOCOL_PATTERN.test(url) ? `http://${url}` : url;

type CandidateCardFormControl = FormControl<string | null>;
type CandidateCardFormArray = FormArray<CandidateCardFormControl>;
type CandidateCardForm = Record<InformationField, CandidateCardFormArray>;
type CandidateCardFieldRowInputs = {
  name: string;
  key: InformationField;
  field: CandidateFieldRow[];
  displayInline: boolean;
  copyable: boolean;
  displayTpl: TemplateRef<void>;
  errorText?: string;
  editable: boolean;
  tooltipEnabled?: boolean;
  tooltipText?: string;
};

@Component({
  selector: 'rt-candidate-contact-card',
  imports: [
    CollapsibleCardComponent,
    HeadingComponent,
    TranslatePipe,
    ButtonComponent,
    IconComponent,
    CandidateFieldRowComponent,
    PhoneNumberPipe,
    PushPipe,
    SocialMediaPipe,
    ProfileInformationEditComponent,
    RtTooltip,
    CollapsibleCardAnalyticsDirective,
  ],
  templateUrl: './candidate-contact-card.component.html',
  styleUrl: './candidate-contact-card.component.less',
})
export class CandidateContactCardComponent {
  private readonly creatorsMap: Record<InformationField, () => CandidateCardFormControl> = {
    [InformationField.Phones]: bind(this.createPhoneControl, this),
    [InformationField.Emails]: bind(this.createEmailControl, this),
    [InformationField.SocialLinks]: bind(this.createUrlControl, this),
    [InformationField.Links]: bind(this.createUrlControl, this),
  };
  public readonly InformationField = InformationField;

  public information = input.required<CandidateInformationForm>();
  public countryCode = input<string | null>();
  public candidateId = input.required<number>();
  public anonymized = input<boolean>(false);
  public loading = input<boolean>(false);
  public editable = input<boolean>(false);
  public canSendMessages = input<boolean>(false);
  public sendMessageClick = output();
  public save = output<Partial<Record<InformationField, string[]>>>();

  public formGroup: FormGroup<CandidateCardForm>;
  public phoneTpl = viewChild<TemplateRef<void>>('phoneTpl');
  public emailTpl = viewChild<TemplateRef<void>>('emailTpl');
  public socialLinkTpl = viewChild<TemplateRef<void>>('socialLinkTpl');
  public linkTpl = viewChild<TemplateRef<void>>('linkTpl');
  public anonymizedTpl = viewChild<TemplateRef<void>>('anonymizedTpl');
  public anonymizedSocialLinkTpl = viewChild<TemplateRef<void>>('anonymizedSocialLinkTpl');

  public fieldObjs = computed(() => ({
    phones: this.information().phones.map(phone => ({
      value: phone,
      isValid: isValidPhoneNumber(phone),
    })),
    emails: this.information().emails.map(email => ({ value: email })),
    socialLinks: this.information().socialLinks.map(link => ({
      value: addHttpProtocolToUrl(link),
    })),
    links: this.information().links.map(link => ({ value: addHttpProtocolToUrl(link) })),
  }));

  public hasAnyInvalidPhone = computed(() => this.fieldObjs().phones.some(phone => !phone.isValid));

  public fields: Signal<CandidateCardFieldRowInputs[]> = computed(() => this.setFields());

  public editingName: InformationField | null = null;

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private destroyRef: DestroyRef,
  ) {
    const group: CandidateCardForm = Object.entries(this.creatorsMap).reduce(
      (acc, [key, creatorFn]: [InformationField, () => CandidateCardFormControl]) => ({
        ...acc,
        [key]: this.fb.array([creatorFn()]),
      }),
      <CandidateCardForm>{},
    );
    this.formGroup = this.fb.group(group);

    effect(() => {
      if (this.loading() === false) {
        this.setEditingName(null);
      }
    });
  }

  public setEditingName(field: InformationField | null) {
    if (field) {
      this.resetForm(field, this.information()[field], this.creatorsMap[field]);
    }
    this.editingName = field;

    this.cdRef.detectChanges();
  }

  public ngAfterViewInit() {
    this.setFields();
  }

  public ngOnInit() {
    fromEvent(document, 'keydown', { capture: true })
      .pipe(
        filter((event: KeyboardEvent) => event.code === 'Escape' && !!this.editingName),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: KeyboardEvent) => {
        event.preventDefault();
        this.setEditingName(null);
      });
  }

  public handleAdd(arrayName: InformationField): void {
    (<CandidateCardFormArray>this.formGroup.get(arrayName)).push(this.creatorsMap[arrayName]());
  }

  public handleSave(section: InformationField): void {
    const payload: any = {};
    payload[section] = this.formGroup.get(section)!.value.filter((val: string) => !!val);
    this.save.emit(payload);
  }

  public handleRemove(itemData: { arrayName: string; index: number }): void {
    const { arrayName, index } = itemData;
    (<CandidateCardFormArray>this.formGroup.get(arrayName)).removeAt(index);
  }

  private createPhoneControl(value: string = ''): CandidateCardFormControl {
    return this.fb.control(value, phoneNumberValidatorFn);
  }

  private createUrlControl(value: string = ''): CandidateCardFormControl {
    return this.fb.control(value, [softUrlValidator]);
  }

  private createEmailControl(value: string = ''): CandidateCardFormControl {
    return this.fb.control(value, [emailTrimValidator]);
  }

  private resetForm(
    key: InformationField,
    data: string[] | null = null,
    creator: (d: any) => CandidateCardFormControl,
  ): void {
    this.formGroup.setControl(key, this.createArrayControl(data || [], creator));
  }

  private createArrayControl(
    data: string[],
    creator: (d: any) => CandidateCardFormControl,
  ): CandidateCardFormArray {
    const _data = data.length ? data : [''];
    return this.fb.array(_data.map(d => creator(d)));
  }

  // eslint-disable-next-line max-lines-per-function
  private setFields(): CandidateCardFieldRowInputs[] {
    return [
      {
        name: this.i18n.translate('app.common.email'),
        key: InformationField.Emails,
        field: this.fieldObjs().emails,
        displayInline: false,
        copyable: !this.anonymized(),
        displayTpl: this.anonymized() ? this.anonymizedTpl()! : this.emailTpl()!,
        errorText: this.i18n.translate('app.candidate.email_error'),
        editable: this.editable() && !this.anonymized(),
        tooltipEnabled: this.anonymized(),
        tooltipText: this.i18n.translate('app.candidate.contact_card.anonymized_tooltip'),
      },
      {
        name: this.i18n.translate('app.candidate.phone_short'),
        key: InformationField.Phones,
        field: this.fieldObjs().phones,
        displayInline: false,
        copyable: !this.hasAnyInvalidPhone() && !this.anonymized(),
        displayTpl: this.anonymized() ? this.anonymizedTpl()! : this.phoneTpl()!,
        editable: this.editable() && !this.anonymized(),
        tooltipEnabled: this.anonymized(),
        tooltipText: this.i18n.translate('app.candidate.contact_card.anonymized_tooltip'),
      },
      {
        name: this.i18n.translate('app.candidate.socials'),
        key: InformationField.SocialLinks,
        field: this.fieldObjs().socialLinks,
        displayInline: true,
        copyable: false,
        displayTpl: this.anonymized() ? this.anonymizedSocialLinkTpl()! : this.socialLinkTpl()!,
        editable: this.editable() && !this.anonymized(),
        tooltipEnabled: this.anonymized(),
        tooltipText: this.i18n.translate('app.candidate.contact_card.anonymized_tooltip'),
      },
      {
        name: this.i18n.translate('app.candidate.links'),
        key: InformationField.Links,
        field: this.fieldObjs().links,
        displayInline: false,
        copyable: true,
        displayTpl: this.linkTpl()!,
        editable: this.editable(),
      },
    ];
  }
}
